/** @jsx jsx */
import { jsx } from "@emotion/core";
import React, { useLayoutEffect, useRef } from "react";
import { hasChildren, isFailed, isLoading, isParentTreeItemType, } from "../Utils/ContainerUtil";
import { UniqueContainerId } from "../UniqueContainerId/UniqueContainerId";
import { useVirtual, defaultRangeExtractor } from "react-virtual";
import { VirtualizedListItem } from "./VirtualizedListItem";
import { ESTIMATED_LIST_ITEM_HEIGHT, OVER_SCAN_SIZE } from "./constants";
import { ContainerTreeList } from "./ContainerTreeList";
import { useFocusMode } from "@smartsheet/lodestar-core";
export var ContainerTreeBase = function (_a) {
    var data = _a.data, onClick = _a.onClick, onContextMenu = _a.onContextMenu, onMouseEnter = _a.onMouseEnter, onMouseLeave = _a.onMouseLeave, onExpandClick = _a.onExpandClick, expandedSet = _a.expandedSet, componentOverrides = _a.componentOverrides, selectedSet = _a.selectedSet, refItems = _a.refItems, updateCursor = _a.updateCursor, lastFocussedItemIndex = _a.lastFocussedItemIndex, indentPerTreeLevel = _a.indentPerTreeLevel, _b = _a.overScanSize, overScanSize = _b === void 0 ? OVER_SCAN_SIZE : _b;
    var ListItem = componentOverrides.listItemComponent;
    var parentRef = useRef(null);
    // To track the current range of virtual items
    var rangeRef = useRef(null);
    var focusModeResult = useFocusMode && useFocusMode();
    var rowVirtualizer = useVirtual({
        size: data.length,
        parentRef: parentRef,
        estimateSize: React.useCallback(function () { return ESTIMATED_LIST_ITEM_HEIGHT; }, []),
        // The amount of items to load both behind and ahead of the current window range
        overscan: overScanSize,
        rangeExtractor: React.useCallback(function (range) {
            rangeRef.current = range;
            return defaultRangeExtractor(range);
        }, [])
    });
    // To track the currently focussed item which will be updated
    // from onFocus and onBlur events from VirtualizedListItem
    var _c = React.useState(null), currentlyFocussed = _c[0], setCurrentlyFocussed = _c[1];
    var itemData = rowVirtualizer.virtualItems.map(function (virtualRow) {
        var _a;
        // virtualRow.index holds the array index of item from the ordered data array
        var treeItemData = data[virtualRow.index];
        var uniqueId = new UniqueContainerId(treeItemData.id, treeItemData.containerType);
        var lastFocussedItemData = data[lastFocussedItemIndex];
        var isLastFocussed = (_a = (lastFocussedItemData &&
            new UniqueContainerId(lastFocussedItemData.id, lastFocussedItemData.containerType).equals(uniqueId))) !== null && _a !== void 0 ? _a : false;
        var tabIndex = isLastFocussed ? 0 : -1;
        var isExpanded = expandedSet.has(uniqueId);
        var isParentType = isParentTreeItemType(treeItemData.containerType) ||
            hasChildren(treeItemData.childData);
        var isSelected = Boolean(selectedSet.has(uniqueId));
        var listItemRef = refItems.get(uniqueId);
        return {
            key: uniqueId.toString(),
            virtualRow: virtualRow,
            treeItemData: treeItemData,
            isExpanded: isExpanded,
            isParentType: isParentType,
            isSelected: isSelected,
            listItemRef: listItemRef,
            tabIndex: tabIndex
        };
    });
    var isFocusComingFromOutsideTreeUsingKeyboard = function (event) {
        // We want to do this check only when user is using keyboard to move focus
        // from outside of container-tree into container-tree, in that case
        // we would like to highlight the last focussed element
        // If it is mouse event, we do not care, as user would directly click on the specific item to get focus.
        if (focusModeResult && focusModeResult.getFocusMode() !== "keyboard") {
            return false;
        }
        /**
         * In a Focus Event, the event.relatedTarget property represents the DOM element that is losing focus
         * (i.e., the element that was previously focused before the current element gained focus).
         * This property is useful when you want to know which element was focused before the current one.
         * https://developer.mozilla.org/en-US/docs/Web/API/FocusEvent/relatedTarget
         */
        return isRelatedTargetOutsideTheTree(event.relatedTarget);
    };
    var isRelatedTargetOutsideTheTree = function (relatedTarget) {
        // Root of the container-tree
        var parentElement = parentRef === null || parentRef === void 0 ? void 0 : parentRef.current;
        return (parentElement &&
            relatedTarget &&
            !parentElement.contains(relatedTarget));
    };
    var handleListItemOnFocus = function (itemIndex, event) {
        if (itemIndex !== lastFocussedItemIndex ||
            isFocusComingFromOutsideTreeUsingKeyboard(event)) {
            updateCursor(itemIndex);
            setCurrentlyFocussed(data[itemIndex]);
        }
    };
    var handleOnBlurOnTree = function (event) {
        /**
         * In a blur event, event.relatedTarget represents the DOM element that is gaining focus
         * (i.e., the element that will become focused after the current element loses focus).
         * This property is useful when you want to know where the focus is moving to.
         * https://developer.mozilla.org/en-US/docs/Web/API/FocusEvent/relatedTarget
         */
        var relatedTarget = event.relatedTarget;
        /**
         * If there is no related target or it is outside the parent tree
         * reset the highlighted tree item
         */
        if (!relatedTarget ||
            (relatedTarget && isRelatedTargetOutsideTheTree(relatedTarget))) {
            resetCurrentlyFocussedTreeItem();
        }
    };
    var resetCurrentlyFocussedTreeItem = function () {
        currentlyFocussed && setCurrentlyFocussed(null);
    };
    var handleMouseClick = function (treeItemData, event) {
        if (onClick) {
            onClick(treeItemData, event);
        }
    };
    var scrollToIndex = function (index) {
        rowVirtualizer.scrollToIndex(index);
        /**
         * rowVirtualizer can update the list based on the size size calculation, and first time scrollToInde
         * may not have any effect. So we are calling it twice to make sure it works.
         */
        requestAnimationFrame(function () {
            if (rangeRef.current) {
                var _a = rangeRef.current, start = _a.start, end = _a.end;
                var isVisible = index >= start && index <= end;
                // If not yet visible, scroll to it
                if (!isVisible) {
                    scrollToIndex(index);
                }
            }
        });
    };
    useLayoutEffect(function () {
        scrollToIndex(lastFocussedItemIndex);
    }, [lastFocussedItemIndex]);
    return (jsx("div", { ref: parentRef, css: {
            overflow: "auto",
            height: "100%",
            width: "100%"
        } },
        jsx(ContainerTreeList, { handleOnBlur: handleOnBlurOnTree, css: {
                height: "".concat(rowVirtualizer.totalSize, "px")
            } }, itemData.map(function (eachItem) {
            var isCurrentlyFocussed = (currentlyFocussed === null || currentlyFocussed === void 0 ? void 0 : currentlyFocussed.id) === eachItem.treeItemData.id;
            return (jsx(VirtualizedListItem, { key: eachItem.key, virtualRow: eachItem.virtualRow, treeItemData: eachItem.treeItemData, tabIndex: eachItem.tabIndex, isExpanded: eachItem.isExpanded, isSelected: eachItem.isSelected, listItemRef: eachItem.listItemRef, indentPerTreeLevel: indentPerTreeLevel, onItemClick: handleMouseClick, onItemContextMenu: onContextMenu, onItemMouseEnter: onMouseEnter, onItemMouseLeave: onMouseLeave, onItemFocus: handleListItemOnFocus },
                jsx(ListItem, { treeItemData: eachItem.treeItemData, isSelected: eachItem.isSelected, isExpanded: eachItem.isExpanded, isParentType: eachItem.isParentType, isCurrentlyFocussed: isCurrentlyFocussed, onExpandClick: onExpandClick }),
                eachItem.isParentType &&
                    eachItem.isExpanded &&
                    hasChildren(eachItem.treeItemData.childData) &&
                    renderChildItem(eachItem.treeItemData.childData, componentOverrides)));
        }))));
};
var renderChildItem = function (childData, componentOverrides) {
    if (isLoading(childData)) {
        var LoadingLine = componentOverrides.loadingLine;
        return jsx(LoadingLine, null);
    }
    if (isFailed(childData)) {
        var ErrorNode = componentOverrides.errorNode;
        var _a = childData, reason = _a.reason, text = _a.text;
        return jsx(ErrorNode, { reason: reason, text: text });
    }
};
//# sourceMappingURL=ContainerTreeBase.js.map